import { checkIfExternal } from "qpr-base/utils/url-utils";

export type NavLabelSource = {
  title?: string | null;
  link?: string | null;
  /** Config may supply a path string or a prebuilt React node. */
  icon?: unknown;
};

function getPageIdFromLink(link: string): string | null {
  if (!link || checkIfExternal(link)) return null;
  const clean = link.startsWith("/") ? link.slice(1) : link;
  const parts = clean.split("/").filter(Boolean);
  const pagePart = parts.find((p) => !/^\d{10}$/.test(p));
  return pagePart ?? parts[parts.length - 1] ?? null;
}

function navMatchKey(item: NavLabelSource): { pageId: string; title: string } {
  return {
    pageId: (getPageIdFromLink(item.link || "") || "").toLowerCase(),
    title: String(item.title || "").trim().toLowerCase(),
  };
}

/**
 * Static English display names for primary nav items (landing + megamenu).
 * Avoids raw `navItems.*` keys and renames Strategy Map / Reporting / workflows.
 */
export function getNavItemDisplayLabel(item: NavLabelSource): string {
  const { pageId, title: key } = navMatchKey(item);
  const title = String(item.title || "").trim();

  if (
    pageId === "strategymap" ||
    pageId === "overview" ||
    key === "strategy map" ||
    key === "strategymap" ||
    key === "overview"
  ) {
    return "Overview";
  }
  if (
    pageId === "reporting" ||
    pageId === "reports" ||
    key === "reporting" ||
    key === "reports"
  ) {
    return "Reports";
  }
  if (
    pageId === "workflows" ||
    pageId === "mytasks" ||
    pageId === "taskworkflow" ||
    key === "workflows" ||
    key === "workflow" ||
    key === "my tasks" ||
    key === "mytasks"
  ) {
    return "My Tasks";
  }

  return title || "—";
}

/**
 * Public icon overrides from `client/public/icons/navitems` (no Mongo /api/files).
 * Paths match the SVG files installed from the repo root.
 */
export function getNavItemIconPath(item: NavLabelSource): string | null {
  const { pageId, title } = navMatchKey(item);

  if (
    pageId === "adminpanel" ||
    title === "admin panel" ||
    title === "adminpanel"
  ) {
    return "/icons/navitems/AdminPanel.svg";
  }
  if (
    pageId === "overview" ||
    pageId === "strategymap" ||
    title === "overview" ||
    title === "strategy map" ||
    title === "strategymap"
  ) {
    return "/icons/navitems/Overview.svg";
  }
  if (
    pageId === "reporting" ||
    pageId === "reports" ||
    title === "reporting" ||
    title === "reports"
  ) {
    return "/icons/navitems/Reports.svg";
  }
  if (
    pageId === "workflows" ||
    pageId === "mytasks" ||
    pageId === "taskworkflow" ||
    title === "workflows" ||
    title === "workflow" ||
    title === "my tasks" ||
    title === "mytasks"
  ) {
    return "/icons/navitems/MyTasks.svg";
  }
  if (
    pageId === "executivedashboard" ||
    pageId === "executivedashboards" ||
    title === "executive dashboards" ||
    title === "executive dashboard" ||
    title === "executivedashboard"
  ) {
    return "/icons/navitems/ExecutiveDashboards.svg";
  }
  if (
    pageId === "workflowadmin" ||
    title === "workflow admin" ||
    title === "workflowadmin"
  ) {
    return "/icons/navitems/WorkflowAdmin.svg";
  }
  if (
    title === "divisional details" ||
    title === "divisionaldetails" ||
    title === "divisions details" ||
    title === "divisionsdetails"
  ) {
    return "/icons/navitems/Sectors.svg";
  }

  return null;
}

/**
 * Absolute URL for a file under `client/public/icons/navitems/` (CRA public folder).
 */
export function getPublicNaviconsUrl(fileName: string): string {
  const name = String(fileName || "").split("/").pop() || "";
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/icons/navitems/${name}`;
}

/**
 * Absolute app URL for overridden icons from `client/public` (no Mongo /api/files).
 * Returns null when the item should keep using the files API / config icon.
 */
export function getPublicNavItemIconUrl(item: NavLabelSource): string | null {
  const path = getNavItemIconPath(item);
  if (!path) return null;
  const fileName = path.split("/").pop() || "";
  return getPublicNaviconsUrl(fileName);
}

/** Filenames served from client public for the primary nav/toolbar set. */
export const PUBLIC_NAVITEM_ICON_FILES = new Set([
  "AdminPanel.svg",
  "AdminPanelSettings.svg",
  "Overview.svg",
  "Reports.svg",
  "MyTasks.svg",
  "ExecutiveDashboards.svg",
  "WorkflowAdmin.svg",
  "Sectors.svg",
]);

/**
 * Resolve toolbar/page icon src: public nav icons skip Mongo `/api/files`.
 */
export function resolveToolbarIconSrc(
  iconPath: string,
  fileApiUrl: (path: string) => string
): string {
  const raw = String(iconPath || "").trim();
  if (!raw) return "";
  const fileName = raw.split("/").pop()?.split("?")[0] || "";
  if (PUBLIC_NAVITEM_ICON_FILES.has(fileName)) {
    return getPublicNaviconsUrl(fileName);
  }
  return fileApiUrl(raw);
}

export function resolveNavItemIconPath(item: NavLabelSource): string {
  const override = getNavItemIconPath(item);
  if (override) return override;
  return typeof item.icon === "string" ? item.icon : "";
}
